/**
 * Query keys do domínio sales (apps/sales + packages/sales-core).
 *
 * Padrão herdado do `@pps/sales-core` (já usava `["sales", ...]` namespaced).
 * Esta factory consolida o shape e expõe builders tipados para evitar typos
 * de invalidation (ex: `["sales", "lead"]` vs `["sales", "leads"]`).
 */
import { tuple } from "./core";
import type { QueryFilters } from "./types";

const root = ["sales"] as const;

const leads = {
  /** Invalida TODAS as queries de leads (lista, detalhes, filtros). */
  all: tuple(root, "leads"),
  /** Lista paginada/filtrada de leads de uma organização. */
  list: <F extends QueryFilters>(orgId: string | null | undefined, filters?: F) =>
    tuple(root, "leads", "list", orgId, filters),
  /** Detalhe de um lead. */
  byId: (leadId: string | null | undefined) => tuple(root, "leads", "detail", leadId),
} as const;

const deals = {
  all: tuple(root, "deals"),
  list: <F extends QueryFilters>(orgId: string | null | undefined, filters?: F) =>
    tuple(root, "deals", "list", orgId, filters),
  byId: (dealId: string | null | undefined) => tuple(root, "deals", "detail", dealId),
} as const;

const pipelines = {
  all: tuple(root, "pipelines"),
  byOrg: (orgId: string | null | undefined) => tuple(root, "pipelines", "by-org", orgId),
} as const;

const opportunities = {
  /** Invalida todas as queries de oportunidades (CRM legacy). */
  all: tuple(root, "opportunities"),
  /** Lista de oportunidades de um clube. */
  byClub: (clubId: string | null | undefined) =>
    tuple(root, "opportunities", "by-club", clubId),
  /** Oportunidades vinculadas a um lead. */
  byLead: (leadId: string | null | undefined) =>
    tuple(root, "opportunities", "by-lead", leadId),
  /**
   * Dados do RECIBO DE VENDA de uma oportunidade (oportunidade, contrato de
   * locação, horários, comprador). Sob `opportunities`: invalidar as
   * oportunidades refaz o recibo aberto.
   */
  reciboDaOportunidade: (opportunityId: string | null | undefined) =>
    tuple(root, "opportunities", "recibo", opportunityId),
  /**
   * Alçada de desconto (20261019161000): a lista de pessoas que o clube
   * autorizou e o "eu posso aprovar?" do usuário logado. Sob
   * `discountApprovers`: mexer na lista invalida as duas.
   */
  discountApprovers: {
    all: tuple(root, "opportunities", "discount-approvers"),
    byClubs: (clubIds: readonly string[]) =>
      tuple(root, "opportunities", "discount-approvers", "by-clubs", [...clubIds].sort().join(",")),
    canApprove: (clubId: string | null | undefined, userId: string | null | undefined) =>
      tuple(root, "opportunities", "discount-approvers", "can-approve", clubId, userId),
  },
} as const;

/**
 * Membros da organização do Sales (fase 3) e o "o que EU posso nesta org"
 * (`sales.my_access`). Mexer em membro invalida os dois: quem se rebaixa perde
 * botão na hora.
 */
const members = {
  all: tuple(root, "members"),
  byOrg: (orgId: string | null | undefined) => tuple(root, "members", "by-org", orgId),
  /** Papel e permissões do usuário logado na org (sob `members`). */
  myAccess: (orgId: string | null | undefined) => tuple(root, "members", "my-access", orgId),
} as const;

/** Integrações de webhook (Lead Ads, CAPI…): linhas da org e a visão efetiva (com herança). */
const webhookIntegrations = {
  all: tuple(root, "webhook_integrations"),
  effective: tuple(root, "webhook_integrations_effective"),
} as const;

/**
 * WhatsApp comercial pelo gateway QR (fase 5): status da sessão, QR do
 * pareamento e a configuração das boas-vindas. Conectar/desconectar invalida `all`.
 */
const whatsappQr = {
  all: tuple(root, "whatsapp_qr"),
  status: (orgId: string | null | undefined) => tuple(root, "whatsapp_qr", "status", orgId),
  qr: (orgId: string | null | undefined) => tuple(root, "whatsapp_qr", "qr", orgId),
  boasVindas: (orgId: string | null | undefined) => tuple(root, "whatsapp_qr", "boas_vindas", orgId),
} as const;

/**
 * Conversas da Inbox. Só o prefixo: as listas do `useInbox` usam
 * `["sales", "conversations", orgId, filtros]`, que este prefixo invalida.
 */
const conversations = {
  all: tuple(root, "conversations"),
  /** Uma conversa pelo id, da org (abrir por link na Inbox). Fica sob `all`: invalidar a lista invalida esta. */
  byId: (orgId: string | null | undefined, conversationId: string | null | undefined) =>
    tuple(root, "conversations", orgId, "by_id", conversationId),
} as const;

/** Mensagens da Inbox, por conversa (o mesmo formato que o useInbox já usava). */
const messages = {
  all: tuple(root, "messages"),
  byConversation: (conversationId: string | null | undefined) => tuple(root, "messages", conversationId),
} as const;

/**
 * ROI (fase 5b): as RPCs `sales.roi_do_periodo`/`sales.funil_do_periodo` e o gasto com anúncio.
 * Sincronizar o gasto ou mudar um negócio invalida `all`.
 */
const roi = {
  all: tuple(root, "roi"),
  periodo: (orgId: string | null | undefined, filtros: Record<string, unknown>) =>
    tuple(root, "roi", "periodo", orgId, filtros),
  funil: (orgId: string | null | undefined, filtros: Record<string, unknown>) =>
    tuple(root, "roi", "funil", orgId, filtros),
  gasto: (orgId: string | null | undefined) => tuple(root, "roi", "gasto", orgId),
} as const;

export const sales = {
  all: root,
  leads,
  deals,
  pipelines,
  opportunities,
  members,
  webhookIntegrations,
  whatsappQr,
  conversations,
  messages,
  roi,
} as const;
